import React, { useState } from "react";
import styled from "@emotion/styled";
import BasicButton from "@components/button/BasicButton";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import FormTitle from "./FormTitle";
import useJoinStepStore from "@store/join/useJoinStepStore";
import axios from "axios";
import {
  CHECK_EMAIL_URL,
  EMAIL_VERIFICATION_URL,
  EMAIL_VERIFICATION_STATUS_URL,
} from "@libs/apiUrl";
import { z } from "zod";

const emailValidation = z.string().email("올바른 이메일 형식이 아닙니다.");

export default function EmailVerificationStep() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const { increaseStep } = useJoinStepStore();
  const language = window.localStorage.getItem("language") || "ko";

  const handleSendVerification = async () => {
    setError("");

    // 이메일 형식 검증
    const result = emailValidation.safeParse(email);
    if (!result.success) {
      setError("올바른 이메일 형식이 아닙니다.");
      return;
    }

    setIsLoading(true);
    try {
      // 1. 이메일 중복 체크
      const checkRes = await axios.get(
        `${CHECK_EMAIL_URL}/check-exists?email=${email}`,
      );
      const emailExists = checkRes.data?.data?.exists ?? false;

      if (emailExists) {
        setError("이미 가입된 이메일입니다.");
        setIsLoading(false);
        return;
      }

      // 2. 인증 메일 발송 (service: "inventory"로 mgmt URL 링크 생성)
      await axios.post(EMAIL_VERIFICATION_URL, {
        email,
        language,
        service: "inventory",
      });

      // 3. 이메일 sessionStorage에 저장
      sessionStorage.setItem("signup_email", email);

      // 4. 발송 완료 상태로 전환
      setIsSent(true);
      setIsModalOpen(true);
    } catch (err: any) {
      const errorCode = err?.response?.data?.error_code;
      if (errorCode === "1008") {
        setError("이메일 인증이 만료되었습니다. 다시 시도해주세요.");
      } else {
        setError("인증 메일 발송에 실패했습니다. 다시 시도해주세요.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    setError("");
    setIsCheckingVerification(true);
    try {
      const res = await axios.get(
        `${EMAIL_VERIFICATION_STATUS_URL}?email=${email}`,
      );
      const verified = res.data?.data?.verified ?? false;

      if (verified) {
        increaseStep();
      } else {
        setError(
          "아직 이메일 인증이 완료되지 않았습니다. 이메일의 인증 링크를 클릭해주세요.",
        );
      }
    } catch {
      setError("인증 상태 확인에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsCheckingVerification(false);
    }
  };

  const handleModalConfirm = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <FormTitle>{"이메일 인증을 진행해주세요."}</FormTitle>
      <Container>
        <LabelText>
          이메일 <Required>*</Required>
        </LabelText>
        <InputWrapper>
          <Input
            type="email"
            inputMode="email"
            placeholder="이메일 입력"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError("");
              if (isSent) setIsSent(false);
            }}
            readOnly={isSent}
          />
          {!isSent ? (
            <BasicButton
              title="인증하기"
              height="5.2rem"
              width="8.3rem"
              padding="1.6rem 1.2rem"
              type="button"
              onClick={handleSendVerification}
              disabled={!email || isLoading}
            />
          ) : (
            <BasicButton
              title="재발송"
              height="5.2rem"
              width="8.3rem"
              padding="1.6rem 1.2rem"
              type="button"
              onClick={handleSendVerification}
              disabled={isLoading}
            />
          )}
        </InputWrapper>
        {error && <ErrorText>{error}</ErrorText>}

        {isSent && (
          <>
            <GuideText>
              입력한 이메일로 인증 링크가 발송되었습니다.
              <br />
              이메일의 인증 링크를 클릭하여 인증을 완료한 후, 아래 버튼을 눌러
              다음 단계로 진행해주세요.
            </GuideText>
            <BasicButton
              title="인증 완료 확인"
              height="5.2rem"
              width="100%"
              padding="1.6rem 1.2rem"
              type="button"
              onClick={handleCheckVerification}
              disabled={isCheckingVerification}
            />
          </>
        )}
      </Container>

      <BasicModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        activeEvent={handleModalConfirm}
        activeName="확인"
        notice={true}
        padding="2rem"
      >
        <ModalContent
          mainMessage="인증 링크가 발송되었습니다."
          subMessage="이메일을 확인하여 인증 링크를 클릭해주세요."
        />
      </BasicModal>
    </>
  );
}

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
});

const LabelText = styled.label(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.medium,
  color: "#000",
}));

const Required = styled.span({
  color: "#FF4808",
});

const InputWrapper = styled.div({
  display: "flex",
  gap: "1rem",
  marginTop: "0.4rem",
});

const Input = styled.input(({ theme }) => ({
  flex: 1,
  height: "5.2rem",
  border: "1px solid #e0e0e0",
  borderRadius: "0",
  padding: "0 1.6rem",
  fontSize: "1.4rem",
  fontFamily: "Pretendard, sans-serif",
  outline: "none",
  "&:focus": {
    borderColor: "#000",
  },
  "&::placeholder": {
    color: "#bdbdbd",
  },
  "&:read-only": {
    backgroundColor: "#f5f5f5",
    color: "#757575",
  },
}));

const ErrorText = styled.p({
  color: "#FF4808",
  fontSize: "1.2rem",
  marginTop: "0.4rem",
});

const GuideText = styled.p({
  color: "#9e9e9e",
  fontSize: "1.2rem",
  lineHeight: "1.8rem",
  marginTop: "1.6rem",
});
